import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useQuery } from '@tanstack/react-query'
import { Col, Row, Tabs, Button, Tooltip, Spin, Space } from 'antd'
import { ReadOutlined, DownloadOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons'
import { PowerBIEmbed } from 'powerbi-client-react'
import { models } from 'powerbi-client'
import { getDashboardServices } from '../../Redux/Services/Administrator/Dashboard'
import config from '../../config'
import '../../App.css'
import '../../Styles/Routes/Deck.css'

const Deck = () => {
    const [activeTab, setActiveTab] = useState('lectura')
    const [reloadKey, setReloadKey] = useState(0)
    const [isRefreshing, setIsRefreshing] = useState(false)
    const reportRef = useRef(null)
    const reportDescargaRef = useRef(null)

    const {
        rex_data_dashboard,
    } = useSelector(state => state.dashboard)

    const dispatch = useDispatch()

    const dashboardData = useQuery({
        queryKey: ['dashboard'],
        queryFn: () => getDashboardServices(dispatch),
        retry: 1,
        refetchOnWindowFocus: false,
    })

    console.log('Dashboard data:', JSON.stringify(rex_data_dashboard, null, 2))
    console.log('Public URL:', rex_data_dashboard?.publico?.url)
    console.log('Private data:', rex_data_dashboard?.privado)

    // Configuración para PowerBI Embed (solo para tab descarga/privado)
    const getPrivateEmbedConfig = () => {
        const privateData = rex_data_dashboard?.privado
        console.log('Private config data:', privateData)

        if (!privateData || !privateData.token || !privateData.workspaceId || !privateData.reportId) {
            console.log('Missing private data for PowerBI embed')
            return null
        }

        const { token, workspaceId, reportId } = privateData

        return {
            type: 'report',
            id: reportId,
            embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${reportId}&groupId=${workspaceId}`,
            accessToken: token,
            tokenType: models.TokenType.Embed,
            settings: {
                panes: {
                    filters: { expanded: false, visible: false },
                    pageNavigation: { visible: false },
                },
                background: models.BackgroundType.Transparent,
                layoutType: models.LayoutType.FitToWidth,
                customLayout: { displayOption: models.DisplayOption.FitToPage },
            },
        }
    }

    // URL público para iframe (tab lectura)
    const getPublicUrl = () => {
        const publicUrl = rex_data_dashboard?.publico?.url
        console.log('Public URL:', publicUrl)
        return publicUrl || null
    }

    // Manejadores de eventos
    const handleEmbedded = () => {
        console.log('Report embedded successfully')
    }

    const handleError = (error) => {
        console.error('PowerBI embed error:', error)
    }

    const handleLoaded = () => {
        console.log('Report loaded successfully')
    }

    const handleRendered = () => {
        console.log('Report rendered successfully')
    }

    const handleTabChange = (key) => {
        setActiveTab(key)
        // Forzar re-renderizado cuando se cambie de tab
        setTimeout(() => {
            setReloadKey(prev => prev + 1)
        }, 100)
    }

    const triggerDashboardRefresh = async () => {
        const timestamp = new Date().toISOString()
        console.log(`[${timestamp}] INICIO: Disparando refresh de Power BI...`)
        setIsRefreshing(true)

        try {
            const refreshUrl = config.api_url + 'utilities/update-dashboard-deck'
            console.log(`[${timestamp}] URL del endpoint: ${refreshUrl}`)

            const response = await fetch(refreshUrl, {
                mode: 'cors',
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Content-type': 'application/json',
                    'usutoken': localStorage.getItem('usutoken'),
                },
            })

            const data = await response.json()
            console.log(`[${timestamp}] RESPUESTA del servidor:`, data)

            if (data.response) {
                console.log(`[${timestamp}] ✓ Refresh de Power BI disparado exitosamente`)
                console.log(`[${timestamp}] Estado del dashboard:`, data.dashboard_status)
            } else {
                console.warn(`[${timestamp}] ⚠ El servidor respondió con response=false`)
                console.warn(`[${timestamp}] Mensaje:`, data.message)
                console.warn(`[${timestamp}] request_id:`, data.request_id)
                console.warn(`[${timestamp}] Detalle del error:`, data.error_details)
            }
        } catch (error) {
            console.error(`[${timestamp}] ✗ Error al disparar refresh:`, error)
        } finally {
            setIsRefreshing(false)
            console.log(`[${timestamp}] FIN: Proceso de refresh completado`)
        }
    }

    const handleReload = () => {
        const timestamp = new Date().toISOString()
        console.log(`[${timestamp}] Usuario hizo click en botón Recargar`)

        console.log('Reloading BI dashboard...')
        setReloadKey(prev => prev + 1)

        // Disparar refresh de Power BI
        triggerDashboardRefresh()

        // Limpiar referencias anteriores
        if (window.report) {
            try {
                window.report = null
            } catch (error) {
                console.log('Error clearing report reference:', error)
            }
        }

        if (window.reportDescarga) {
            try {
                window.reportDescarga = null
            } catch (error) {
                console.log('Error clearing reportDescarga reference:', error)
            }
        }
    }

    // NO se dispara refresh al montar la página: Power BI limita la cantidad de
    // refreshes por día del dataset (el backend chequea "Limit Exceeded"). Si cada
    // visita a /deck gatillara uno, se agota la cuota y deja de actualizarse.
    // El refresh corre automáticamente tras cada carga de archivo (FileUpload.js)
    // y a demanda con el botón de esta página.
    useEffect(() => {
        return () => {
            if (window.report) window.report = null
            if (window.reportDescarga) window.reportDescarga = null
        }
    }, [])

    const privateEmbedConfig = getPrivateEmbedConfig()
    const publicUrl = getPublicUrl()

    // Configuración de los tabs
    const tabItems = [
        {
            key: 'lectura',
            label: (
                <span>
                    <ReadOutlined className="tab-icon" />
                    Lectura
                </span>
            ),
            children: (
                <div className="deck-tab-pane">
                    {dashboardData.isLoading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <div className="loading-text">Cargando información de lectura...</div>
                        </div>
                    ) : publicUrl ? (
                        <div className="powerbi-container" key={`public-${reloadKey}`}>
                            <iframe
                                title="Dashboard de Lectura"
                                src={publicUrl}
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                allowFullScreen
                                style={{ borderRadius: '12px' }}
                                key={`iframe-${reloadKey}`}
                            />
                        </div>
                    ) : (
                        <div className="loading-container">
                            <div className="loading-text">No hay datos de lectura disponibles</div>
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'descarga',
            label: (
                <span>
                    <DownloadOutlined className="tab-icon" />
                    Descarga
                </span>
            ),
            children: (
                <div className="deck-tab-pane">
                    {dashboardData.isLoading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <div className="loading-text">Cargando información de descarga...</div>
                        </div>
                    ) : privateEmbedConfig ? (
                        <div className="powerbi-container" key={`private-${reloadKey}`}>
                            <PowerBIEmbed
                                key={`powerbi-${reloadKey}`}
                                embedConfig={privateEmbedConfig}
                                eventHandlers={
                                    new Map([
                                        ['loaded', handleLoaded],
                                        ['rendered', handleRendered],
                                        ['error', handleError],
                                        ['embedded', handleEmbedded]
                                    ])
                                }
                                cssClassName="power-bi-embed"
                                style={{ height: "100%" }}
                                getEmbeddedComponent={(embeddedReport) => {
                                    window.reportDescarga = embeddedReport
                                    reportDescargaRef.current = embeddedReport
                                }}
                            />
                        </div>
                    ) : (
                        <div className="loading-container">
                            <div className="loading-text">No hay datos de descarga disponibles</div>
                        </div>
                    )}
                </div>
            )
        }
    ]

    return (
        <div className="deck-container">
            <Row>
                <Col span={24}>
                    <div className="deck-header">
                        <div className="deck-header-content">
                            <div className="deck-header-text">
                                <h1 className="deck-title">DECK</h1>
                                <p className="deck-subtitle">Panel de control y análisis de datos</p>
                            </div>
                            <div className="deck-header-actions">
                                <Space>
                                    <Tooltip title={isRefreshing ? "Actualizando Power BI..." : "Refrescar Power BI y recargar dashboard"}>
                                        <Button
                                            type="primary"
                                            shape="circle"
                                            icon={isRefreshing ? <SyncOutlined spin /> : <ReloadOutlined />}
                                            onClick={handleReload}
                                            disabled={isRefreshing}
                                            className="reload-button"
                                            loading={isRefreshing}
                                        />
                                    </Tooltip>
                                </Space>
                            </div>
                        </div>
                    </div>
                </Col>
                <Col span={24}>
                    <div className="deck-tabs-container">
                        <Tabs
                            activeKey={activeTab}
                            onChange={handleTabChange}
                            items={tabItems}
                            className="deck-tabs"
                            tabBarGutter={0}
                            size="large"
                        />
                    </div>
                </Col>
            </Row>
        </div>
    )
}

export default Deck